import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Tag, Space, message, Popconfirm, Modal, Form, Input } from 'antd';
import { ShopOutlined, DeleteOutlined, EyeOutlined, CloudDownloadOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../api';
import { BRAND_PRIMARY } from '../utils/brandColors';
import ShopifyIcon from './Utils/ShopifyIcon';
import WooCommerceIcon from './Utils/WooCommerceIcon';

const MyStores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [importGuideActive, setImportGuideActive] = useState(false);
  const [guideModalShown, setGuideModalShown] = useState(false);
  const [guidePopVisible, setGuidePopVisible] = useState(false);
  const [guidePromptHandled, setGuidePromptHandled] = useState(false);
  const [wooConnectModalVisible, setWooConnectModalVisible] = useState(false);
  const [wooConnectForm] = Form.useForm();
  const [wooConnecting, setWooConnecting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStores();
  }, []);

  useEffect(() => {
    const shouldGuide = Boolean(location.state?.importGuide);
    setImportGuideActive(shouldGuide);
    if (!shouldGuide) {
      setGuideModalShown(false);
      setGuidePopVisible(false);
      setGuidePromptHandled(false);
    }
  }, [location.state]);

  const fetchStores = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/stores/integration`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const normalizedStores = (response.data.data || []).map((entry) => {
          const storeData = entry.data || {};
          return {
            id: entry.id,
            dropshipper_id: entry.dropshipper_id,
            platform: entry.platform || storeData.platform,
            shop_domain: entry.shop_domain || storeData.shop_domain,
            created_at: entry.created_at,
            updated_at: entry.updated_at,
            data: storeData,
          };
        });
        setStores(normalizedStores);
      }
    } catch (error) {
      
      message.error('Failed to fetch store list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!importGuideActive || loading) {
      return;
    }
    
    if (stores.length === 0) {
      if (!guideModalShown) {
        setGuideModalShown(true);
        Modal.info({
          title: 'Import Orders',
          content: 'Please connect your stores to import orders.',
          onOk: () => {},
        });
      }
      return;
    }

    if (!guidePromptHandled) {
      setGuidePopVisible(true);
      setGuidePromptHandled(true);
    }
  }, [importGuideActive, loading, stores, guideModalShown, guidePromptHandled]);

  const handleConnectShopify = () => {
    navigate('/dropshipper/connect-shopify');
  };

  const handleViewDetail = (store) => {
    setSelectedStore(store);
    setDetailModalVisible(true);
  };

  const handleOpenImportOrders = (store) => {
    if (!store) return;
    const params = new URLSearchParams({
      platform: store.platform,
      shopDomain: store.shop_domain,
    });

    navigate(`/dropshipper/import-third-party-store-order?${params.toString()}`);
  };

  const handleDisconnectStore = async (platform, shopDomain) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/stores/integration/${platform}/${shopDomain}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success('Store disconnected successfully');
      fetchStores(); // Refresh list
    } catch (error) {
      
      message.error('Failed to disconnect store');
    }
  };

  const handleConnectWooCommerce = () => {
    setWooConnectModalVisible(true);
    wooConnectForm.resetFields();
  };

  const handleWooCommerceConnect = async () => {
    try {
      const values = await wooConnectForm.validateFields();
      setWooConnecting(true);
      
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/stores/integration/woocommerce`, values, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      message.success('WooCommerce store connected successfully');
      setWooConnectModalVisible(false);
      fetchStores(); // Refresh the list
    } catch (error) {
      
      const errorDetail = error.response?.data?.detail || 'Failed to connect WooCommerce store';
      message.error(errorDetail);
    } finally {
      setWooConnecting(false);
    }
  };

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'shopify':
        return <ShopifyIcon />;
      case 'woocommerce':
        return <WooCommerceIcon />;
      default:
        return <ShopOutlined />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      width: '15%',
      render: (platform) => {
        let style = {};
        if (platform === 'woocommerce') {
          style = { background: '#f3e5f5', borderColor: '#9c27b0', color: '#6a1b9a' };
        } else if (platform === 'shopify') {
          style = { backgroundColor: '#f0f9f0', borderColor: '#96bf48', color: '#2d5a1d' };
        } else {
          style = { backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: '#000000' };
        }
        return (
          <Tag 
            icon={getPlatformIcon(platform)}
            style={style}
          >
            {platform.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Store Domain',
      dataIndex: 'shop_domain',
      key: 'shop_domain',
      width: '30%',
      ellipsis: true,
      render: (domain) => (
        <a href={`https://${domain}`} target="_blank" rel="noopener noreferrer">
          {domain}
        </a>
      ),
    },
    {
      title: 'Connected At',
      dataIndex: 'installed_at',
      key: 'installed_at',
      width: '20%',
      render: (_, record) => formatDate(record?.data?.installed_at),
    },
    {
      title: 'Status',
      key: 'status',
      width: '15%',
      render: () => (
        <Tag color="green">Connected</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: '20%',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button 
            type="primary" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleViewDetail(record)}
          >
            Details
          </Button>
          {(() => {
            const isGuideTarget = importGuideActive
              && stores.length > 0
              && record.platform === stores[0]?.platform
              && record.shop_domain === stores[0]?.shop_domain;

            const handleImportClick = () => {
              if (isGuideTarget && guidePopVisible) {
                return;
              }
              handleOpenImportOrders(record);
            };

            const importButton = (
              <Button
                icon={<CloudDownloadOutlined />}
                size="small"
                onClick={handleImportClick}
              >
                Import Orders
              </Button>
            );

            if (!isGuideTarget) {
              return importButton;
            }

            return (
              <Popconfirm
                open={guidePopVisible}
                title="Click this button to import existing orders from your store."
                okText="Got it"
                showCancel={false}
                onCancel={() => {
                  setGuidePopVisible(false);
                  handleOpenImportOrders(record);
                }}
                onOpenChange={(visible) => {
                  if (!visible) {
                    setGuidePopVisible(false);
                  }
                }}
              >
                {importButton}
              </Popconfirm>
            );
          })()}

          <Popconfirm
            title="Are you sure you want to disconnect this store?"
            onConfirm={() => handleDisconnectStore(record.platform, record.shop_domain)}
            okText="Confirm"
            cancelText="Cancel"
          >
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
            >
              Disconnect
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card 
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ShopOutlined style={{ marginRight: 8, color: BRAND_PRIMARY }} />
            My Stores
          </div>
        }
        extra={
          <Space>
            <Button 
              type="primary" 
              icon={<ShopifyIcon />}
              onClick={handleConnectShopify}
              style={{ background: '#f0f9f0', borderColor: '#96bf48', color: '#3e7e27ff' }}
            >
              Connect Shopify Store
            </Button>
            <Button 
              type="primary" 
              icon={<WooCommerceIcon />}
              onClick={handleConnectWooCommerce}
              style={{ background: '#f3e5f5', borderColor: '#9c27b0', color: '#6a1b9a' }}
            >
              Connect WooCommerce Store
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={stores}
          loading={loading}
          rowKey={(record) => record.id ?? `${record.platform}-${record.shop_domain}`}
          locale={{
            emptyText: 'No connected stores. Click the button above to connect your Shopify store'
          }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} stores`,
          }}
          size="middle"
        />
      </Card>

      {/* Store Details Modal */}
      <Modal
        title={`${selectedStore?.platform?.toUpperCase()} Store Details`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedStore && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <p><strong>Store Domain:</strong> {selectedStore.shop_domain}</p>
              <p><strong>Platform:</strong> 
                <Tag 
                  style={{ 
                    marginLeft: 8, 
                    backgroundColor: '#f0f9f0', 
                    borderColor: '#96bf48', 
                    color: '#3e7e27ff' 
                  }}
                >
                  {selectedStore.platform?.toUpperCase()}
                </Tag>
              </p>
              <p><strong>Connected At:</strong> {formatDate(selectedStore?.data?.installed_at)}</p>
              <p><strong>Granted Scopes:</strong> {selectedStore?.data?.granted_scopes || 'None'}</p>
            </Card>
            
            <Card size="small" title="Technical Information">
              <p><strong>Token Status:</strong> 
                <Tag color={selectedStore?.data?.token ? 'green' : 'red'}>
                  {selectedStore?.data?.token ? 'Valid' : 'Invalid'}
                </Tag>
              </p>
              <p><strong>Token (Encrypted):</strong> {selectedStore?.data?.token ? '••••••••••••••••' : 'None'}</p>
            </Card>
          </div>
        )}
      </Modal>

      {/* WooCommerce Connect Modal */}
      <Modal
        title="Connect WooCommerce Store"
        open={wooConnectModalVisible}
        onCancel={() => setWooConnectModalVisible(false)}
        onOk={handleWooCommerceConnect}
        confirmLoading={wooConnecting}
        okText="Connect"
        width={600}
      >
        <Form
          form={wooConnectForm}
          layout="vertical"
        >
          <Form.Item
            name="shop_domain"
            label="Store URL"
            rules={[
              { required: true, message: 'Please input your WooCommerce store domain' }
            ]}
          >
            <Input addonBefore="https://" placeholder="your-store.com" />
          </Form.Item>
          
          <Form.Item
            name="consumer_key"
            label="Consumer Key"
            rules={[{ required: true, message: 'Please input your Consumer Key' }]}
          >
            <Input placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxx" />
          </Form.Item>
          
          <Form.Item
            name="consumer_secret"
            label="Consumer Secret"
            rules={[{ required: true, message: 'Please input your Consumer Secret' }]}
          >
            <Input.Password placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxx" />
          </Form.Item>
          
          <Form.Item
            name="webhook_secret"
            label="Webhook Secret"
            rules={[{ required: true, message: 'Please input your Webhook Secret' }]}
          >
            <Input.Password placeholder="whs_xxxxxxxxxxxxxxxxxxxxxxxx" />
          </Form.Item>
          
          <div style={{ marginTop: 16, padding: 12, background: '#f0f2f5', borderRadius: 4 }}>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
              <strong>Steps to setup WooCommerce and obtain credentials:</strong><br />
              1. Go to WooCommerce Admin → WooCommerce → Settings → Advanced → REST API<br />
              2. Click "Add key" to generate new API credentials<br />
              3. Set the permissions to "Read/Write"<br />
              4. Copy the Consumer Key and Consumer Secret<br />
              5. Webhook Secret: Go to WooCommerce → Settings → Advanced → Webhooks → Add Webhook<br />
              6. Create an order webhook with topic "Order created" and set a Secret. <br />
              (Webhook URL: https://api-demo.fafadrop.com/api/v1/webhook/woocommerce/orders)
            </p>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default MyStores;
